import { TENANT_IMAGE_BASE_URL } from '@kfc-global/react-shared/config/config.constants';
import { config } from '@kfc-global/react-shared/config/config.utils';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { ButtonComp } from 'atoms/Buttons';
import ImageComponent from 'atoms/Image';
import { login } from 'common/utilities/LoginUtils';
import { getFullImagePath } from 'common/utilities/utils';
import TimerComponent from 'organisms/CouponsLanding/Presentation/TimerComponent';
import { getValidFormatDate, isTimeValid } from 'organisms/CouponsLanding/utils/CouponUtils';
import React, { useState } from 'react';
import { ReactSVG } from 'react-svg';
import FindStoreImage from 'styles/staticAssets/images/Native_select a kfc_grey image.svg';
import RedFilledRibbon from 'styles/staticAssets/images/RedFilled_Ribbon.svg';
import ClockSVG from 'styles/staticAssets/images/TimerIcon.svg';
import ribbonRedOutlined from 'styles/staticAssets/images/TimerRibbon.svg';
import { DealsDetailModal } from '../../DealsAndOffersLanding/Presentation/DealsDetailModal';
import { handleRedeemClick } from 'organisms/OffersAndPromotions/OffersAndPromotionsUtilities/offersAndPromotionsUtils';
import {
  DEFAULT_IMAGE_PATH,
  FIND_A_KFC,
  KFC_LOCAL_OFFERS,
  LOGIN,
  OFFER_APPLIED,
  REDEEM,
  SIGN_IN_EXCLUSIVE_OFFERS,
  VALID_TO,
  VIEW_DETAILS,
} from '../constants/offersAndDealsConstants';

const OfferDealsCard = props => {
  const {
    couponApplyHandler,
    data = {},
    tenantId = '',
    id = '',
    onStartOrderClick,
    showViewDetailsButton = false,
    tenantData,
  } = props;
  const [isDealModalOpen, openDealModal] = useState(false);
  const openDealModalFunc = () => {
    openDealModal(true);
  };

  const closeModalFunc = () => {
    openDealModal(false);
  };

  const getRibbon = dataInfo => {
    if (dataInfo?.showTimer && isTimeValid(dataInfo?.endTime)) {
      const header = (
        <span data-testid='coupon-time-block' className='coupon-time-block'>
          <img className='coupon-time-icon' src={ClockSVG} alt='Coupon Time Icon' />
          <TimerComponent endTime={dataInfo?.endTime} />
        </span>
      );
      return (
        <div className='offersRibbon-container'>
          <ReactSVG src={ribbonRedOutlined} className='offers-ribbon timer' />
          <span className='ribbon-text time-text'>{header}</span>
        </div>
      );
    } else if (dataInfo?.header) {
      const header = dataInfo?.header;
      return (
        <div className='offersRibbon-container'>
          <ReactSVG src={RedFilledRibbon} className='offers-ribbon' />
          <span className='ribbon-text text-white'>{header}</span>
        </div>
      );
    }
  };
  /**
   * Method to render cards with image and text
   * @param {Object} data - Data source to render coupon details
   * @param {Number} id - Unique Id
   * @returns JSX.Element
   */
  const getImageComponent = (dataInfo, idVal) => (
    <div key={idVal} data-testid='offers-deals-card-container' className='medium-card-offers-deals'>
      {getRibbon(dataInfo)}
      <ImageComponent
        data-testid='offers-deals-card-image'
        className='offers-menu-product-full-image'
        srcFile={getFullImagePath(dataInfo?.imageSrc, tenantData, false, false, true)}
        alt={'No-Image'}
        defaultPath={`${config(TENANT_IMAGE_BASE_URL)}${tenantId}${DEFAULT_IMAGE_PATH}`}
      />
      <div data-testid='offers-deals-card-footer' className='offer-button-container'>
        <button
          className='view-details-btn image-full-text-btn'
          onClick={() => openDealModalFunc()}
          data-testid='details-button'
          disabled={!showViewDetailsButton}
        >
          {translateWithI18Next(VIEW_DETAILS)}
        </button>
        <button
          className='redeem-btn redeem-button-full-image-text'
          onClick={() => handleRedeemClick(dataInfo?.discountCode, couponApplyHandler)}
          disabled={dataInfo?.isApplied}
          data-testid='redeem-button'
        >
          <span data-testid='btn-txt' className='btn-text'>
            {dataInfo?.isApplied ? translateWithI18Next(OFFER_APPLIED) : translateWithI18Next(REDEEM)}
          </span>
        </button>
      </div>
    </div>
  );

  /**
   * Method to render cards with image only
   * @param {Object} data - Data source to render coupon details
   * @param {Number} id - Unique Id
   * @returns JSX.Element
   */
  const getImageTextComponent = (dataInfo, idVal) => (
    <div key={idVal} data-testid='offers-deals-card-container' className='medium-card-offers-deals'>
      {getRibbon(data)}
      <ImageComponent
        data-testid='offers-deals-card-image'
        className='offers-menu-product-image'
        srcFile={getFullImagePath(dataInfo?.imageSrc, tenantData, false, false, true)}
        alt={'No-Image'}
        defaultPath={`${config(TENANT_IMAGE_BASE_URL)}${tenantId}${DEFAULT_IMAGE_PATH}`}
      />
      <div className='offers-card-content' data-testid='offers-deals-card-header'>
        <h4 className='offers-deals-product-title'>{dataInfo?.heading}</h4>
        <h5 className='offers-deals-product-desc'>{dataInfo?.description}</h5>
      </div>
      <div data-testid='offers-deals-card-footer' className='offer-button-container'>
        <ButtonComp
          disabled={!showViewDetailsButton}
          data-testid='details-button'
          className='view-details-btn'
          onClick={() => openDealModalFunc()}
        >
          {translateWithI18Next(VIEW_DETAILS)}
        </ButtonComp>
        <ButtonComp
          onClick={() => handleRedeemClick(dataInfo?.discountCode, couponApplyHandler)}
          disabled={dataInfo?.isApplied}
          className='redeem-btn'
          data-testid='redeem-button'
        >
          <span className='btn-text'>
            {dataInfo?.isApplied ? translateWithI18Next(OFFER_APPLIED) : translateWithI18Next(REDEEM)}
          </span>
        </ButtonComp>
      </div>
    </div>
  );

  /**
   * Method to render cards without image and text only
   * @param {Object} data - Data source to render coupon details
   * @param {Number} id - Unique ID
   * @returns JSX.Element
   */
  const getTextComponent = (dataInfo, idVal) => (
    <div key={idVal} data-testid='offers-deals-card-container' className='medium-card-offers-deals'>
      {getRibbon(dataInfo)}
      <div className='offers-card-content-text'>
        <div className='offers-card-title-desc' data-testid='offers-deals-card-header'>
          <h4 className='offers-deals-product-title'>{dataInfo?.heading}</h4>
          <h5 className='offers-deals-product-desc'>{dataInfo?.description}</h5>
          {dataInfo?.endTime && (
            <h5 className='offers-deals-product-valid'>
              {`${translateWithI18Next(VALID_TO)} ${getValidFormatDate(dataInfo?.endTime)}`}
            </h5>
          )}
        </div>
        <div className='offer-button-container' data-testid='offers-deals-card-footer'>
          <ButtonComp
            data-testid='details-button'
            disabled={!showViewDetailsButton}
            onClick={() => openDealModalFunc()}
            className='view-details-btn'
          >
            {translateWithI18Next(VIEW_DETAILS)}
          </ButtonComp>
          <ButtonComp
            data-testid='redeem-button'
            onClick={() => handleRedeemClick(dataInfo?.discountCode, couponApplyHandler)}
            disabled={dataInfo?.isApplied}
            className='redeem-btn'
          >
            <span className='btn-text'>
              {dataInfo?.isApplied ? translateWithI18Next(OFFER_APPLIED) : translateWithI18Next(REDEEM)}
            </span>
          </ButtonComp>
        </div>
      </div>
    </div>
  );

  const getStoreCard = () => (
    <div data-testid='offers-deals-card-container' className='medium-card-offers-deals store-card'>
      <img src={FindStoreImage} className='offers-store-image' alt='' />
      <h3 className='offer-store-text'>{translateWithI18Next(KFC_LOCAL_OFFERS)}</h3>
      <div className='offer-store-btn-container'>
        <ButtonComp className='store-btn' onClick={onStartOrderClick} ariaLable={translateWithI18Next(FIND_A_KFC)}>
          {translateWithI18Next(FIND_A_KFC)}
        </ButtonComp>
      </div>
    </div>
  );

  const getLoginCard = () => (
    <div data-testid='offers-deals-card-container' className='medium-card-offers-deals login-card'>
      <h3 className='offer-login-text'>{translateWithI18Next(SIGN_IN_EXCLUSIVE_OFFERS)}</h3>
      <div className='offer-login-btn-container'>
        <ButtonComp
          className='login-btn'
          data-testid='login-btn'
          onClick={login}
          ariaLable={translateWithI18Next(LOGIN)}
        >
          {translateWithI18Next(LOGIN)}
        </ButtonComp>
      </div>
    </div>
  );

  const renderCardTypeContainer = () => {
    switch (data.cardType) {
      case 'image-text':
        return getImageTextComponent(data, id);
      case 'image':
        return getImageComponent(data, id);
      case 'text':
        return getTextComponent(data, id);
      case 'store':
        return getStoreCard();
      case 'login':
        return getLoginCard();
        //no default
    }
  };

  return (
    <React.Fragment>
      {renderCardTypeContainer()}
      {isDealModalOpen && (
        <DealsDetailModal
          data-testid='deals-modal'
          data={data}
          isDealModalOpen={isDealModalOpen}
          handleClose={closeModalFunc}
          couponApplyHandler={couponApplyHandler}
        />
      )}
    </React.Fragment>
  );
};

export default OfferDealsCard;
